import { MoreOutlined } from "@ant-design/icons";
import {
  Affix,
  Button,
  Card,
  Drawer,
  Dropdown,
  Form,
  Menu,
  Modal,
  Space,
} from "antd";
import ListTable from "common/ListTable";
import { PROBLEM_REMEDY_MAP } from "apiServices/API_ENDPOINTS";
import { deleteData, getData, postData, putData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import CustomFilter from "components/Common/CustomFilter";
import Header from "components/Common/Header";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import {
  PROBLEM_REMEDY_MAP_DELETE_PERMISSION,
  PROBLEM_REMEDY_MAP_EDIT_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";
import AddAndEditForm from "./AddAndEditForm";

export default function RemedyMap() {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;
  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  const [selectedRecord, setSelectedRecord] = useState(null);

  const onFinish = async (values) => {
    try {
      let response = null;
      if (selectedRecord) {
        response = await putData(
          `${PROBLEM_REMEDY_MAP}/${selectedRecord?.id}`,
          values,
        );
      } else {
        response = await postData(PROBLEM_REMEDY_MAP, values);
      }

      if (response && response?.code === 200) {
        setIsOpen(false);
        setSelectedRecord(null);
        form.resetFields();
        fetchData({
          page: pagination.current,
          per_page: pagination.pageSize,
        });
        alertPop("success", response?.message || "Success");
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const fetchData = async (filter) => {
    setLoading(true);
    setFetchError(null);
    let response = await getData(`${PROBLEM_REMEDY_MAP}`, false, filter);
    if (response && response?.status === 200) {
      let masterData = response?.data?.data?.data;
      setData(masterData);
      setPagination({
        current: filter?.page,
        pageSize: filter?.pageSize,
        total: response?.data?.data?.total || 0,
      });
    } else {
      setFetchError("Could not load this list.");
      alertPop(
        "error",
        response?.message ? response?.message : "Something Went Wrong!",
      );
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData({
      page: pagination.current,
      per_page: pagination.pageSize,
    });
  }, [pagination.current, pagination.pageSize]);

  const handleTableChange = (pagination) => {
    setPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    });
  };

  const menu = (data) => (
    <Menu
      items={[
        {
          label: (
            <span
              role="button"
              onClick={() => {
                setIsOpen(true);
                setSelectedRecord(data);
              }}
            >
              Edit
            </span>
          ),
          key: 1,
          disabled: !hasPermission([PROBLEM_REMEDY_MAP_EDIT_PERMISSION]),
        },
        {
          label: (
            <span
              role="button"
              className="text-danger"
              onClick={() => {
                Modal.confirm({
                  title: "Are you sure you want to delete this item?",
                  content: "This action cannot be undone.",
                  okText: "Yes",
                  okType: "danger",
                  cancelText: "No",
                  onOk: async () => {
                    let response = await deleteData(
                      `${PROBLEM_REMEDY_MAP}/${data?.id}`,
                    );
                    if (response && response?.code === 200) {
                      fetchData(pagination.current, pagination.pageSize);
                      alertPop("success", response?.message || "Success");
                    } else {
                      alertPop(
                        "error",
                        response?.message
                          ? response?.message
                          : "Something Went Wrong!",
                      );
                    }
                  },
                });
              }}
            >
              Delete
            </span>
          ),
          key: 2,
          disabled: !hasPermission([PROBLEM_REMEDY_MAP_DELETE_PERMISSION]),
        },
      ]}
    />
  );

  const memoizedCustomFilter = useMemo(
    () => (
      <CustomFilter
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        tableData={data}
        fetchTableData={fetchData}
        filterName="remedy_map_filter"
      />
    ),
    [showFilter],
  );

  return (
    <>
      <Affix offsetTop={0}>
        <Card className="p-fixed">
          <Header
            title="Remedy Map"
            actions={[
              <Affix offsetTop={20}>
                <Space>
                  <Button
                    type={showFilter ? "danger" : "primary"}
                    size="small"
                    onClick={() => setShowFilter((prev) => !prev)}
                    style={{ float: "right" }}
                  >
                    {`${showFilter ? "Close" : "Open"} Filter Option`}
                  </Button>
                  <Button
                    size="small"
                    key={2}
                    type="primary"
                    onClick={() => {
                      setIsOpen(true);
                      setSelectedRecord(null);
                    }}
                  >
                    Add New
                  </Button>
                </Space>
              </Affix>,
            ]}
          ></Header>
        </Card>
      </Affix>
      <Card>
        {memoizedCustomFilter}
        <ListTable
          style={{ marginTop: "20px" }}
          error={fetchError}
          onRetry={() =>
            fetchData({
              page: pagination.current,
              per_page: pagination.pageSize,
            })
          }
          emptyText="No records to show yet."
          dataSource={data}
          columns={[
            {
              title: "Department",
              dataIndex: ["department", "title"],
              key: "department_title",
            },
            {
              title: "Problem Type",
              dataIndex: ["problem_type", "name"],
              key: "problem_type_name",
            },
            {
              title: "Problem",
              dataIndex: ["problem", "name"],
              key: "problem_name",
            },
            {
              title: "Reason",
              dataIndex: ["reason", "name"],
              key: "reason_name",
            },
            {
              title: "Remedy",
              dataIndex: ["remedy", "name"],
              key: "remedy_name",
            },

            {
              title: "Action",
              render: (_, record) => (
                <div className="cursore-pointer">
                  <Dropdown overlay={menu(record)}>
                    <MoreOutlined style={{ fontSize: "20px" }} />
                  </Dropdown>
                </div>
              ),
            },
          ]}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
          }}
          loading={loading}
          onChange={handleTableChange}
        />
      </Card>
      <Drawer
        title={selectedRecord ? "Edit" : "Add New"}
        placement="right"
        width={400}
        onClose={() => {
          setIsOpen(false);
          setSelectedRecord(null);
        }}
        visible={isOpen}
        closable={false}
        destroyOnClose={true}
        maskClosable={false}
        extra={
          <Space>
            <Button
              size="small"
              key={1}
              danger
              onClick={() => {
                setIsOpen(false);
                setSelectedRecord(null);
                form.resetFields();
              }}
            >
              Discard
            </Button>
            <Button
              size="small"
              key={2}
              type="primary"
              onClick={() => form.submit()}
            >
              Submit
            </Button>
          </Space>
        }
      >
        <AddAndEditForm form={form} data={selectedRecord} onFinish={onFinish} />
      </Drawer>
    </>
  );
}
